/**
 * src/components/Financial/modules/GannSquareLineModule.jsx
 * 【修正版：ラベル表示 (0, 12...144) 対応・設定保存対応】
 * 下辺と右辺に12刻みのラベルを表示
 */
import React, { useMemo, useEffect } from "react";
import { Maximize, Info } from "lucide-react";

/**
 * ギャン・スクエア (144) 計算ロジック
 */
export const calculateGannSquare144 = function (baseIndex, basePrice, config) {
  if (
    baseIndex === null ||
    baseIndex === undefined ||
    basePrice === null ||
    basePrice === undefined
  )
    return null;

  var scale = config.scale; // 価格変動幅（高さそのもの）
  var direction = config.direction;
  var squareWidth = config.squareWidth || 144;

  // スクエアの終点計算 (時間軸)
  var endIndex = baseIndex + squareWidth;

  // 価格の高さ計算
  var heightPrice = scale * (direction === "up" ? 1 : -1);
  var endPrice = basePrice + heightPrice;

  return {
    start: { x: baseIndex, y: basePrice },
    end: { x: endIndex, y: endPrice },
    width: squareWidth,
    height: heightPrice,
  };
};

/**
 * ギャン・スクエア レイヤーコンポーネント
 */
export const GannSquareLineLayer = function (props) {
  var show = props.show;
  var baseIndex = props.baseIndex;
  var basePrice = props.basePrice;
  var config = props.config;
  var coords = props.coords;
  var displayRange = props.displayRange;
  var isDarkMode = props.isDarkMode;

  // 設定のマージ
  var cfg = useMemo(
    function () {
      return Object.assign(
        {
          scale: 10000,
          direction: "up",
          squareWidth: 144,

          // 表示フラグと色設定
          showBox: true,
          boxColor: isDarkMode ? "#4ade80" : "#16a34a",

          showGrid: true,
          gridColor: isDarkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",

          showMainDiagonals: true,
          mainColor: isDarkMode ? "#4ade80" : "#16a34a",

          showFans: false,
          fanColor: isDarkMode ? "#f87171" : "#dc2626",

          // ★追加: ラベル設定
          showLabels: true,
          labelColor: isDarkMode ? "#4ade80" : "#16a34a",
        },
        config || {},
      );
    },
    [config, isDarkMode],
  );

  var square = useMemo(
    function () {
      return calculateGannSquare144(baseIndex, basePrice, cfg);
    },
    [baseIndex, basePrice, cfg],
  );

  if (!show || !square || !coords || !coords.getX || !coords.getY) {
    return null;
  }

  // 表示範囲の開始位置（ドラッグ補正用）
  var startOffset = displayRange ? displayRange.start : 0;

  var getX = coords.getX;
  var getY = coords.getY;

  // 座標変換
  var x1 = getX(square.start.x - startOffset);
  var y1 = getY(square.start.y);

  var x2 = getX(square.end.x - startOffset);
  var y2 = getY(square.end.y);

  var w = x2 - x1;
  var h = y2 - y1;

  // スタイル定義
  const createDashStyle = (color, width = 1, dashArray = "4, 3") => ({
    stroke: color,
    strokeWidth: width,
    strokeDasharray: dashArray,
    fill: "none",
    opacity: 0.5,
  });

  var boxStyle = createDashStyle(cfg.boxColor, 1.5, "2, 2");
  var mainStyle = createDashStyle(cfg.mainColor, 1.2, "2, 2");
  var gridStyle = createDashStyle(cfg.gridColor, 0.8, "2, 2");
  var fanStyle = createDashStyle(cfg.fanColor, 1, "2, 2");

  // ★ラベル用の数値リスト生成 (0, 12, 24 ... 144)
  var labelSteps = [];
  for (var i = 0; i <= 144; i += 12) {
    labelSteps.push(i);
  }

  // ラベル位置の基準
  var visualBottomY = Math.max(y1, y2); // 画面上の下端
  var visualRightX = Math.max(x1, x2); // 画面上の右端

  return (
    <g className="gann-square-144-layer" pointerEvents="none">
      {/* 1. 外枠 (Box) */}
      {cfg.showBox && (
        <rect
          x={Math.min(x1, x2)}
          y={Math.min(y1, y2)}
          width={Math.abs(w)}
          height={Math.abs(h)}
          {...boxStyle}
        />
      )}

      {/* 2. グリッド (Grid) */}
      {cfg.showGrid && (
        <g>
          {[1 / 8, 2 / 8, 3 / 8, 4 / 8, 5 / 8, 6 / 8, 7 / 8, 1 / 3, 2 / 3].map(
            (r, i) => (
              <React.Fragment key={"grid-" + i}>
                <line
                  x1={x1 + w * r}
                  y1={y1}
                  x2={x1 + w * r}
                  y2={y2}
                  {...gridStyle}
                />
                <line
                  x1={x1}
                  y1={y1 + h * r}
                  x2={x2}
                  y2={y1 + h * r}
                  {...gridStyle}
                />
              </React.Fragment>
            ),
          )}
        </g>
      )}

      {/* 3. 主要アングル (Main Diagonals) */}
      {cfg.showMainDiagonals && (
        <g>
          {/* クロス & ダイヤモンド */}
          <line x1={x1} y1={y1} x2={x2} y2={y2} {...mainStyle} />
          <line x1={x1} y1={y2} x2={x2} y2={y1} {...mainStyle} />
          <line
            x1={x1 + w / 2}
            y1={y1}
            x2={x2}
            y2={y1 + h / 2}
            {...mainStyle}
          />
          <line
            x1={x2}
            y1={y1 + h / 2}
            x2={x1 + w / 2}
            y2={y2}
            {...mainStyle}
          />
          <line
            x1={x1 + w / 2}
            y1={y2}
            x2={x1}
            y2={y1 + h / 2}
            {...mainStyle}
          />
          <line
            x1={x1}
            y1={y1 + h / 2}
            x2={x1 + w / 2}
            y2={y1}
            {...mainStyle}
          />

          {/* 1x2, 2x1 */}
          <line x1={x1} y1={y2} x2={x1 + w / 2} y2={y1} {...mainStyle} />
          <line x1={x1} y1={y2} x2={x2} y2={y1 + h / 2} {...mainStyle} />
          <line x1={x1} y1={y1} x2={x1 + w / 2} y2={y2} {...mainStyle} />
          <line x1={x1} y1={y1} x2={x2} y2={y1 + h / 2} {...mainStyle} />
          <line x1={x2} y1={y2} x2={x2 - w / 2} y2={y1} {...mainStyle} />
          <line x1={x2} y1={y2} x2={x1} y2={y1 + h / 2} {...mainStyle} />
          <line x1={x2} y1={y1} x2={x2 - w / 2} y2={y2} {...mainStyle} />
          <line x1={x2} y1={y1} x2={x1} y2={y1 + h / 2} {...mainStyle} />
        </g>
      )}

      {/* 4. マイナーファン (Fans) */}
      {cfg.showFans && (
        <g>
          {[1 / 8, 1 / 4, 1 / 3, 2 / 3, 3 / 4, 7 / 8].map((r, i) => (
            <React.Fragment key={"fans-" + i}>
              <line x1={x1} y1={y2} x2={x1 + w * r} y2={y1} {...fanStyle} />
              <line x1={x1} y1={y1} x2={x1 + w * r} y2={y2} {...fanStyle} />
              <line x1={x1} y1={y2} x2={x2} y2={y2 - h * r} {...fanStyle} />
              <line x1={x1} y1={y1} x2={x2} y2={y1 + h * r} {...fanStyle} />

              <line x1={x2} y1={y2} x2={x2 - w * r} y2={y1} {...fanStyle} />
              <line x1={x2} y1={y1} x2={x2 - w * r} y2={y2} {...fanStyle} />
              <line x1={x2} y1={y2} x2={x1} y2={y2 - h * r} {...fanStyle} />
              <line x1={x2} y1={y1} x2={x1} y2={y1 + h * r} {...fanStyle} />
            </React.Fragment>
          ))}
        </g>
      )}

      {/* ★ 5. ラベル表示 (0, 12 ... 144) */}
      {cfg.showLabels &&
        labelSteps.map((step) => {
          var ratio = step / 144;

          // A. 下辺 (Time Axis)
          var tx = x1 + w * ratio;
          var ty = visualBottomY + 12; // 少し下に配置

          // B. 右辺 (Price Axis)
          var px = visualRightX + 6; // 少し右に配置
          var py = y1 + h * ratio; // 始点(y1)から終点(y2)へ補間
          var priceVal = basePrice + square.height * ratio;

          return (
            <g key={"lbl-" + step}>
              {/* Time Label (Bottom) */}
              <text
                x={tx}
                y={ty}
                fontSize="9"
                fill={cfg.labelColor}
                fontWeight="normal"
                textAnchor="middle"
                dominantBaseline="hanging"
                style={{
                  textShadow: isDarkMode ? "0 0 2px #000" : "0 0 2px #fff",
                }}
              >
                {step}
              </text>

              {/* Price Label (Right) */}
              <text
                x={px}
                y={py}
                fontSize="9"
                fill={cfg.labelColor}
                fontWeight="normal"
                textAnchor="start"
                dominantBaseline="middle"
                style={{
                  textShadow: isDarkMode ? "0 0 2px #000" : "0 0 2px #fff",
                }}
              >
                {step} (
                {priceVal.toLocaleString(undefined, {
                  maximumFractionDigits: 1,
                })}
                )
              </text>
            </g>
          );
        })}

      {/* タイトルラベル */}
      <text
        x={x1}
        y={y1 - 10}
        fontSize="11"
        fill={cfg.boxColor}
        fontWeight="bold"
        textAnchor="start"
        style={{
          textShadow: isDarkMode ? "0 0 2px #000" : "0 0 2px #fff",
        }}
      >
        GS 144 (Scale: {cfg.scale})
      </text>

      <circle cx={x1} cy={y1} r={3} fill={cfg.boxColor} />
    </g>
  );
};

/**
 * 設定UI
 */
export const GannSquareLineSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var isDarkMode = props.isDarkMode;
  var config = props.config;
  var setConfig = props.setConfig;

  useEffect(
    function () {
      if (setConfig) {
        setConfig(function (prev) {
          var defaults = {
            scale: 10000,
            direction: "up",
            squareWidth: 144,

            showBox: true,
            boxColor: isDarkMode ? "#4ade80" : "#16a34a",

            showGrid: true,
            gridColor: isDarkMode ? "#9ca3af" : "#64748b",

            showMainDiagonals: true,
            mainColor: isDarkMode ? "#4ade80" : "#16a34a",

            showFans: false,
            fanColor: isDarkMode ? "#f87171" : "#dc2626",

            // ★デフォルト設定追加
            showLabels: true,
            labelColor: isDarkMode ? "#4ade80" : "#16a34a",
          };
          return Object.assign({}, defaults, prev || {});
        });
      }
    },
    [setConfig], // 初回のみ
  );

  var updateConfig = function (key, value) {
    if (setConfig) {
      setConfig(function (prev) {
        return Object.assign({}, prev, { [key]: value });
      });
    }
  };

  var currentCfg = config || {};

  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
    inputBg: isDarkMode ? "#0b0e14" : "#ffffff",
    inputBorder: isDarkMode ? "#374151" : "#cbd5e1",
    isDarkMode: isDarkMode,
  };

  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  var inputStyle = {
    width: "100px",
    padding: "6px",
    borderRadius: "4px",
    border: "1px solid " + theme.inputBorder,
    background: theme.inputBg,
    color: theme.text,
    textAlign: "right",
    fontSize: "12px",
    outline: "none",
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "bold",
            color: theme.text,
            fontSize: "15px",
          }}
        >
          <Maximize size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>Gann Square of 144</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.2s",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            alignItems: "flex-end",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "11px", color: theme.subText }}>
              価格スケール
            </label>
            <input
              type="number"
              step="1000"
              value={currentCfg.scale || 10000}
              onChange={function (e) {
                updateConfig("scale", parseFloat(e.target.value));
              }}
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "11px", color: theme.subText }}>
              方向
            </label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={function () {
                  updateConfig("direction", "up");
                }}
                style={btnSt(currentCfg.direction === "up")}
              >
                UP
              </button>
              <button
                onClick={function () {
                  updateConfig("direction", "down");
                }}
                style={btnSt(currentCfg.direction === "down")}
              >
                DOWN
              </button>
            </div>
          </div>
        </div>

        <hr
          style={{ border: "none", borderTop: "1px solid " + theme.border }}
        />

        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <ConfigRow
            label="Box Frame"
            enabled={currentCfg.showBox}
            color={currentCfg.boxColor || "#4ade80"}
            onToggle={(v) => updateConfig("showBox", v)}
            onColorChange={(c) => updateConfig("boxColor", c)}
            theme={theme}
          />
          <ConfigRow
            label="Main Diagonals"
            enabled={currentCfg.showMainDiagonals}
            color={currentCfg.mainColor || "#4ade80"}
            onToggle={(v) => updateConfig("showMainDiagonals", v)}
            onColorChange={(c) => updateConfig("mainColor", c)}
            theme={theme}
          />
          <ConfigRow
            label="Grid (1/3, 1/8)"
            enabled={currentCfg.showGrid}
            color={currentCfg.gridColor || "#9ca3af"}
            onToggle={(v) => updateConfig("showGrid", v)}
            onColorChange={(c) => updateConfig("gridColor", c)}
            theme={theme}
          />
          <ConfigRow
            label="Fans (Red)"
            enabled={currentCfg.showFans}
            color={currentCfg.fanColor || "#f87171"}
            onToggle={(v) => updateConfig("showFans", v)}
            onColorChange={(c) => updateConfig("fanColor", c)}
            theme={theme}
          />
          {/* ★ラベル設定追加 */}
          <ConfigRow
            label="Labels (0..144)"
            enabled={currentCfg.showLabels}
            color={currentCfg.labelColor || "#4ade80"}
            onToggle={(v) => updateConfig("showLabels", v)}
            onColorChange={(c) => updateConfig("labelColor", c)}
            theme={theme}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          color: theme.subText,
          fontSize: "10px",
          marginTop: "20px",
          paddingTop: "15px",
          borderTop: "1px solid " + theme.border,
        }}
      >
        <Info size={14} />
        <span>
          スクエアの下辺と右辺に12刻みのラベルを表示します。色は設定パネルで変更可能です。
        </span>
      </div>
    </div>
  );
};

/**
 * 設定行コンポーネント
 */
const ConfigRow = function ({
  label,
  enabled,
  color,
  onToggle,
  onColorChange,
  theme,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: enabled
          ? theme.isDarkMode
            ? "rgba(255,255,255,0.05)"
            : "#fff"
          : "transparent",
        padding: "6px 12px",
        borderRadius: "8px",
        border: "1px solid " + (enabled ? color : theme.border),
        transition: "all 0.2s",
        opacity: enabled ? 1 : 0.6,
      }}
    >
      <input
        type="checkbox"
        checked={!!enabled}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ cursor: "pointer" }}
      />
      <span
        style={{
          fontSize: "11px",
          fontWeight: "bold",
          color: enabled ? theme.text : theme.subText,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <input
        type="color"
        value={color || "#000000"}
        onChange={(e) => onColorChange(e.target.value)}
        disabled={!enabled}
        style={{
          width: "20px",
          height: "20px",
          border: "none",
          padding: 0,
          background: "none",
          cursor: enabled ? "pointer" : "default",
          marginLeft: "4px",
        }}
      />
    </div>
  );
};
